import secureLog from './secureLogger';
import { getConfig } from './secureConfig';

/**
 * Comprehensive diagnostic tool for analytics tracking
 * @returns {Object} Diagnostic information about analytics configuration
 */
export const checkAnalyticsSetup = () => {
  // Check environment variables
  const envVars = {
    REACT_APP_GOOGLE_ANALYTICS_ID: process.env.REACT_APP_GOOGLE_ANALYTICS_ID,
    REACT_APP_GA_ID: process.env.REACT_APP_GA_ID,
    REACT_APP_FACEBOOK_PIXEL_ID: process.env.REACT_APP_FACEBOOK_PIXEL_ID,
    REACT_APP_FB_PIXEL_ID: process.env.REACT_APP_FB_PIXEL_ID,
    REACT_APP_GTM_ID: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID || process.env.REACT_APP_GTM_ID,
    NODE_ENV: process.env.NODE_ENV
  };
  
  // Check secure config values
  const configValues = {
    'analytics.googleAnalyticsId': getConfig('analytics.googleAnalyticsId'),
    'analytics.fbPixelId': getConfig('analytics.fbPixelId'),
    'analytics.googleTagManagerId': getConfig('analytics.googleTagManagerId')
  };
  
  // Check global objects
  const globalObjects = {
    hasWindow: typeof window !== 'undefined',
    hasDocument: typeof document !== 'undefined',
    hasGtag: typeof window !== 'undefined' && typeof window.gtag === 'function',
    hasDataLayer: typeof window !== 'undefined' && Array.isArray(window.dataLayer),
    hasFbq: typeof window !== 'undefined' && typeof window.fbq === 'function',
    hasHotjar: typeof window !== 'undefined' && typeof window.hj === 'function'
  };
  
  // Check for script tags
  const scriptTags = typeof document !== 'undefined' ? 
    Array.from(document.getElementsByTagName('script')).map(s => s.src) : [];
  
  const hasGtagScript = scriptTags.some(src => src.includes('googletagmanager.com/gtag/js'));
  const hasFbPixelScript = scriptTags.some(src => src.includes('connect.facebook.net/en_US/fbevents.js'));
  const hasHotjarScript = scriptTags.some(src => src.includes('hotjar.com/c/hotjar-'));
  
  // Compile diagnostic info
  const diagnosticInfo = {
    timestamp: new Date().toISOString(),
    environment: process.env.NODE_ENV,
    envVars: {
      hasGoogleAnalyticsId: !!envVars.REACT_APP_GOOGLE_ANALYTICS_ID || !!envVars.REACT_APP_GA_ID,
      hasFacebookPixelId: !!envVars.REACT_APP_FACEBOOK_PIXEL_ID || !!envVars.REACT_APP_FB_PIXEL_ID,
      hasGTMId: !!envVars.REACT_APP_GTM_ID,
      hasHotjarId: !!process.env.REACT_APP_HOTJAR_ID
    },
    configValues: {
      hasGoogleAnalyticsId: !!configValues['analytics.googleAnalyticsId'],
      hasFacebookPixelId: !!configValues['analytics.fbPixelId'],
      hasGTMId: !!configValues['analytics.googleTagManagerId']
    },
    globalObjects,
    scripts: {
      hasGtagScript,
      hasFbPixelScript,
      hasHotjarScript,
      totalScripts: scriptTags.length
    },
    cspViolations: [] // Will be populated by event listener
  };
  
  // Log diagnostic info
  secureLog.info('Analytics Diagnostic Results:', diagnosticInfo);
  
  // Add CSP violation listener
  if (typeof window !== 'undefined') {
    document.addEventListener('securitypolicyviolation', (e) => {
      const violation = {
        blockedURI: e.blockedURI,
        violatedDirective: e.violatedDirective,
        originalPolicy: e.originalPolicy
      };
      diagnosticInfo.cspViolations.push(violation);
      secureLog.warn('CSP Violation detected:', violation);
    });
  }
  
  return diagnosticInfo;
};

/**
 * Initialize Google Analytics directly
 * This is a fallback method to ensure GA is initialized
 */
export const initializeAnalyticsFallback = () => {
  if (typeof window === 'undefined') return false;
  
  try {
    // Get Google Analytics ID from multiple sources
    const gaId = 
      process.env.REACT_APP_GOOGLE_ANALYTICS_ID || 
      process.env.REACT_APP_GA_ID || 
      getConfig('analytics.googleAnalyticsId');
    
    if (!gaId) {
      secureLog.warn('No Google Analytics ID found for fallback initialization');
      return false;
    }
    
    // Check if already initialized
    if (window.gtag && window.dataLayer) {
      secureLog.info('Google Analytics already initialized, skipping fallback');
      return true;
    }
    
    secureLog.info('Initializing Google Analytics fallback with ID:', gaId);
    
    // Create script element
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    
    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    
    gtag('js', new Date());
    gtag('config', gaId, { 
      send_page_view: false,
      debug_mode: process.env.NODE_ENV === 'development',
      transport_url: 'https://www.google-analytics.com'
    });
    
    // Append script to document
    document.head.appendChild(script);
    
    secureLog.info('Google Analytics fallback initialization complete');
    return true;
  } catch (error) {
    secureLog.error('Error in analytics fallback initialization:', error);
    return false;
  }
}; 